import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { LogOut } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";
import { usePublicConfig } from "../lib/config";
import { LangToggle } from "./LangToggle";

export const useAppTitle = () => {
  const config = usePublicConfig();
  const { pick } = useLang();
  const b = config?.branding || {};
  const company = pick(b.company_name_en, b.company_name_ar) || "Inception";
  useEffect(() => { document.title = `MyCard | ${company}`; }, [company]);
};

const navClass = ({ isActive }) =>
  `rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
    isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
  }`;

export const AppShell = () => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  useAppTitle();
  const onLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-alabaster">
      <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-alabaster/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
          <span className="me-auto flex items-center" data-testid="app-brand">
            <img src="/brand/inception-black.png" alt="Inception" className="h-5 w-auto" />
          </span>
          <nav className="flex items-center gap-1" data-testid="main-nav">
            <NavLink to="/me" className={navClass} data-testid="nav-my-card">{t("my_card")}</NavLink>
            {user?.has_card && <NavLink to="/insights" className={navClass} data-testid="nav-insights">{t("insights")}</NavLink>}
            {user?.has_admin_access && <NavLink to="/admin" className={navClass} data-testid="nav-admin">{t("admin")}</NavLink>}
          </nav>
          <LangToggle />
          <button onClick={onLogout} data-testid="logout-button" title={t("sign_out")}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:text-slate-900">
            <LogOut className="h-4 w-4 rtl:-scale-x-100" />
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-5 sm:px-6 sm:py-6">
        <Outlet />
      </main>
    </div>
  );
};
